import {
  Box,
  Checkbox,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableFoot,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import {
  DeleteIcon,
  InformationIcon,
  PencilIcon,
  PlusCircleOutlineIcon,
  PlusIcon,
  SubdirectoryArrowRightIcon,
} from '@atgfr8/icons';
import {
  BasicModal,
  Button,
  LTLProductCatalog,
  Popover,
  TableRowErrors,
  useIndexedPopoverState,
} from '@components';
import { SelectInputCustom } from '@components/Select/SelectInputCustom';
import { ToastType } from '@components/Toast';
import { FeatureFlags } from '@constants/featureFlags';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { LoadItemCalculationInput, LoadLineItemDimensions } from '@store/ltl';
import {
  DEFAULT_LOAD_ITEM,
  DEFAULT_LOAD_ITEM_BREAKDOWN,
} from '@store/ltl/defaultValues';
import { GetCalculationsFromLoadItems } from '@store/ltl/services';
import {
  loadItemCalculationState,
  resetLoadItemCalculation,
  setLoadItemCalculation,
  setLtlQuoteState,
} from '@store/ltl/store';
import { LTLLoadItem, LTLQuoteLoadItemBreakdown } from '@store/ltl/types';
import { closeModal, isModalOpen, openModal } from '@store/modals';
import { LTLCatalogs } from '@store/orders';
import { initialHazmatDetails } from '@store/orders/defaultVals';
// eslint-disable-next-line no-restricted-syntax
import { ConfigManager } from '@utils/ConfigManager';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { toDimensionValue } from '@utils/ltl/dimensions';
import { getTotalWeight, singleWeightTooMuch } from '@utils/ltlUtils';
import { handleToast } from '@utils/utils';
import { HazardousMaterial } from '@views/order/components/carrier/stops/stopsEditor/HazardousMaterial';
import { compact, isEmpty } from 'lodash';
import {
  Accessor,
  Component,
  Index,
  Show,
  batch,
  createEffect,
  createSignal,
  createUniqueId,
  onMount,
} from 'solid-js';

import { LTlFormClasses as cls } from '../classes';
import { LTLWeightLimit, freightClasses, unitTypes } from '../constants';
import { LTlFormStyles } from '../styles';
import { ItemAccessorial, LTLQuotingState } from '../types';
import { ExclusionList } from './ExclusionList';
import { QuoteLoadItemBreakdown } from './QuoteLoadItemBreakdown';
import SuggestLineItemClass from './SuggestLineItemClass';
const cellStyles = {
  padding: '10px',
  'border-bottom': '1px solid rgba(224, 224, 224, 1)',
};

const calculationFieldClasses =
  'w-full bg-[rgba(0,0,0,0.06)]! rounded-t-[4px]! rounded-b-none! border-transparent! hover:border-transparent! focus-within:outline-none! focus-within:border-t-transparent! focus-within:border-x-transparent! focus-within:border-b-2! focus-within:border-b-[#1976d2]! [&>input]:cursor-default [&>input]:text-black! [&>input]:pt-[16px]! [&>.floating-label]:top-[6px]! [&>.floating-label>.strip]:hidden!';
const summaryCalculationFieldClasses = `${calculationFieldClasses} h-[42px]! [&>.floating-label]:top-[8px]! [&>.floating-label]:text-[13px]! [&>.floating-label]:font-medium! [&>input]:pt-[22px]! [&>input]:pb-[8px]! [&>input]:text-[16px]! [&>input]:font-normal!`;

type LTLLoadItemsProps = {
  setFields: (field: string, value: unknown) => void;
  data: Accessor<LTLQuotingState>;
  errors: (field: string) => string | undefined;
  suggestedClassSettingEnabled: () => boolean;
  mixedCommoditiesSettingEnabled: () => boolean;
};

export const LTLLoadItems: Component<LTLLoadItemsProps> = (props) => {
  const setFields = (...args: Parameters<typeof props.setFields>) =>
    props.setFields(...args);

  const [loadItemDimChange, setLoadItemDimChange] = createSignal(false);
  const [loadCalculationsLoading, setLoadCalculationsLoading] =
    createSignal(false);

  // When the LtlPublicApiWarnings flag is on, advisories come from the
  // load-calculation API instead of being recomputed here. `messageType` is the
  // service's severity; error, warning, and info messages render in matching
  // rows. TableRowErrors hides each row when empty. When the flag is off, the
  // legacy client-side checks below render instead.
  const isPublicApiWarningsEnabled = isFeatureFlagEnabled(
    FeatureFlags.LtlPublicApiWarnings,
  );
  const calculationMessages = (severity: 'error' | 'warning' | 'info') =>
    (loadItemCalculationState.items.warnings ?? [])
      .filter((warning) => {
        const messageType = warning.messageType.toLowerCase();
        if (severity === 'error') return messageType === 'error';
        if (severity === 'warning') return messageType === 'warning';
        return messageType === 'info' || messageType === 'information';
      })
      .map((warning) => warning.message);
  const [loadItemIndex, setLoadItemIndex] = createSignal<number | null>(null);
  const instanceId = createUniqueId();
  const productCatalogModalId = (index: number | null = loadItemIndex()) =>
    `sidebar-ltl-product-catalog-${instanceId}${index !== null ? `-${index}` : ''}`;
  const [cachedHazmatDetails, setCachedHazmatDetails] = createSignal<
    Record<number, unknown>
  >({});
  const hazmatPopover = useIndexedPopoverState(
    (index) => `[data-hazmat-row-index="${index}"]`,
  );

  const remapHazmatCacheAfterDelete = (removedIndex: number) => {
    setCachedHazmatDetails((previousCache) => {
      const nextCache: Record<number, unknown> = {};

      for (const [indexKey, hazmatDetail] of Object.entries(previousCache)) {
        const currentIndex = Number(indexKey);
        if (Number.isNaN(currentIndex) || currentIndex === removedIndex) {
          continue;
        }

        const targetIndex =
          currentIndex > removedIndex ? currentIndex - 1 : currentIndex;
        nextCache[targetIndex] = hazmatDetail;
      }

      return nextCache;
    });
  };

  const tableHeaders = [
    {
      title: '# of Units',
      required: true,
      minWidth: '105px',
      maxWidth: '105px',
    },
    {
      title: 'Unit Type',
      required: true,
      minWidth: '120px',
      maxWidth: '120px',
    },
    {
      title: 'Item Description',
      minWidth: '220px',
      maxWidth: '220px',
    },
    {
      title: '# of Pcs',
      minWidth: '90px',
      maxWidth: '90px',
    },
    {
      title: 'Weight',
      required: true,
      minWidth: '110px',
      maxWidth: '110px',
    },
    {
      title: 'Dimensions (in)',
      minWidth: '210px',
      maxWidth: '210px',
    },
    {
      title: 'NMFC',
      minWidth: '120px',
      maxWidth: '120px',
    },
    {
      title: 'Class',
      required: true,
      minWidth: '100px',
      maxWidth: '100px',
    },
    {
      title: 'Hazmat',
      minWidth: '80px',
      maxWidth: '180px',
    },
    {
      title: 'PCF (lb/ft3)',
      minWidth: '100px',
      maxWidth: '100px',
    },
    {
      title: 'Cubic Feet',
      minWidth: '110px',
      maxWidth: '110px',
    },
    {
      title: 'Actions',
    },
  ];

  const createLoadItem = (item: LTLCatalogs) => ({
    quantity: 1,
    item: item.unitType || 'Pallets',
    class: item.class?.toString(),
    description: item.description,
    lengthInch: toDimensionValue(item.lengthInches),
    widthInch: toDimensionValue(item.widthInches),
    heightInch: toDimensionValue(item.heightInches),
    pieces: item.pieces,
    nmfc: item.nmfc,
    hazmat: item.hazmat,
    weight: item.weight ?? null,
    quoteLoadItemBreakdown: [],
    height: null,
    length: null,
    width: null,
    uom: 'lbs',
    loadItemHazmatDetail: item.hazmat
      ? {
          hazardClass: item.hazardClass,
          packingGroup: item.packingGroup,
          unNumber: item.unNumber,
          contactName: item.contactName,
          contactNumber: item.contactNumber,
          piece: item.piece,
        }
      : null,
  });

  const mergeLoadItemFromCatalog = (
    existingItem: LTLLoadItem,
    catalogItem: ReturnType<typeof createLoadItem>,
  ) => {
    const merged = { ...existingItem };
    const fieldsToMerge = [
      'item',
      'class',
      'description',
      'lengthInch',
      'widthInch',
      'heightInch',
      'pieces',
      'nmfc',
      'weight',
    ] as const;

    for (const field of fieldsToMerge) {
      const current = existingItem[field];
      if (current === null || current === undefined || current === '') {
        (merged as Record<string, unknown>)[field] = catalogItem[field];
      }
    }

    // Hazmat: only fill if current is false (default) and catalog has true
    if (!existingItem.hazmat && catalogItem.hazmat) {
      merged.hazmat = catalogItem.hazmat;
      merged.loadItemHazmatDetail = catalogItem.loadItemHazmatDetail;
    }

    // loadItemHazmatDetail: only fill if hazmat is true but detail is missing
    if (existingItem.hazmat && !existingItem.loadItemHazmatDetail) {
      merged.loadItemHazmatDetail = catalogItem.loadItemHazmatDetail;
    }

    return merged;
  };

  const addListItemFromLTLCatalog = (item: LTLCatalogs, index?: number) => {
    const loadItems = props.data().loadItems;
    const newItem = createLoadItem(item);
    if (typeof index === 'number') {
      if (isFeatureFlagEnabled(FeatureFlags.CatalogLineItemFillEmptyOnly)()) {
        const existingItem = loadItems[index];
        const mergedItem = mergeLoadItemFromCatalog(existingItem, newItem);
        const updatedLoadItems = [...loadItems];
        updatedLoadItems[index] = mergedItem;
        setFields('loadItems', updatedLoadItems);
        closeModal(productCatalogModalId(index));
        setLoadItemDimChange(true);
        void handleOnChangeForLinearfeet(index);
        return;
      }

      const updatedLoadItems = [...loadItems];
      updatedLoadItems[index] = newItem;
      setFields('loadItems', updatedLoadItems);
      closeModal(productCatalogModalId(index));
      setLoadItemDimChange(true);
      void handleOnChangeForLinearfeet(index);
      return;
    }
    setLoadItemDimChange(true);
    const isEmptyLoadItem = (li: LTLLoadItem) => {
      const {
        quantity,
        type,
        description,
        pieces,
        weight,
        lengthInch,
        widthInch,
        heightInch,
        nmfc,
        class: classValue,
        hazmat,
        loadItemHazmatDetail,
        quoteLoadItemBreakdown,
      } = li;
      const checks = {
        quantity: isEmpty(quantity),
        type: isEmpty(type),
        description: isEmpty(description),
        pieces: isEmpty(pieces),
        weight: isEmpty(weight),
        lengthInch: isEmpty(lengthInch),
        widthInch: isEmpty(widthInch),
        heightInch: isEmpty(heightInch),
        nmfc: isEmpty(nmfc),
        classValue: isEmpty(classValue),
        hazmat: isEmpty(hazmat),
        loadItemHazmatDetail: isEmpty(loadItemHazmatDetail),
      };
      let breakdownsEmpty = true;
      if (
        Array.isArray(quoteLoadItemBreakdown) &&
        quoteLoadItemBreakdown.length > 0
      ) {
        breakdownsEmpty = quoteLoadItemBreakdown.every((breakdown) => {
          const keysToCheck = [
            'description',
            'pieces',
            'weight',
            'nmfc',
            'class',
            'hazmat',
            'loadItemHazmatDetail',
          ] as (keyof LTLQuoteLoadItemBreakdown)[];
          const breakdownChecks = keysToCheck.reduce(
            (acc, key) => {
              acc[key] = isEmpty(breakdown[key]);
              return acc;
            },
            {} as Record<keyof LTLQuoteLoadItemBreakdown, boolean>,
          );
          return Object.values(breakdownChecks).every(Boolean);
        });
      }
      return Object.values(checks).every(Boolean) && breakdownsEmpty;
    };

    const firstEmptyIndex = loadItems.findIndex(isEmptyLoadItem);

    if (firstEmptyIndex !== -1) {
      const updatedLoadItems = [...loadItems];
      updatedLoadItems[firstEmptyIndex] = newItem;
      setFields('loadItems', updatedLoadItems);
      closeModal(productCatalogModalId(null));

      void handleOnChangeForLinearfeet(firstEmptyIndex);
      return;
    }

    const onlyDefault =
      loadItems.length === 1 &&
      'key' in loadItems[0] &&
      delete loadItems[0]?.key &&
      delete loadItems[0]?.index &&
      JSON.stringify(loadItems[0]) === JSON.stringify(DEFAULT_LOAD_ITEM);

    if (onlyDefault) {
      setFields('loadItems', [newItem]);
    } else {
      setFields('loadItems', [...loadItems, newItem]);
    }
    const newIndex = onlyDefault ? 0 : loadItems.length;
    closeModal(productCatalogModalId(null));
    void handleOnChangeForLinearfeet(newIndex);
  };

  const createErrors = (index: number) => {
    const fields = [
      'quantity',
      'description',
      'weight',
      'lengthInch',
      'widthInch',
      'heightInch',
      'class',
      'loadItemHazmatDetail',
    ];
    const breakdownFields = ['weight', 'class'];
    const breakdowns =
      props.data().loadItems[index]?.quoteLoadItemBreakdown ?? [];
    const breakdownErrors = breakdowns.flatMap((_, breakdownIndex) =>
      compact(
        breakdownFields.map((field) =>
          props.errors(
            `loadItems.${index}.quoteLoadItemBreakdown.${breakdownIndex}.${field}`,
          ),
        ),
      ).map((message) => `Sub-item ${breakdownIndex + 1}: ${message}`),
    );
    return [
      ...compact(
        fields.map((field) => props.errors(`loadItems.${index}.${field}`)),
      ),
      ...breakdownErrors,
    ];
  };

  const handleHazmatChange = (
    rowData: Accessor<LTLLoadItem>,
    index: number,
  ) => {
    const isHazmat = Boolean(rowData().hazmat);
    const hazmatPath = `loadItems.${index}.hazmat`;
    const hazmatDetailPath = `loadItems.${index}.loadItemHazmatDetail`;

    if (isHazmat) {
      const currentHazmatDetail =
        props.data().loadItems[index]?.loadItemHazmatDetail;

      if (currentHazmatDetail) {
        setCachedHazmatDetails((prev) => ({
          ...prev,
          [index]: currentHazmatDetail,
        }));
      }

      setFields(hazmatPath, false);
      setFields(hazmatDetailPath, null);
      hazmatPopover.close();
      return;
    }

    setFields(hazmatPath, true);
    const restoredHazmatDetail = cachedHazmatDetails()[index];
    setFields(hazmatDetailPath, restoredHazmatDetail ?? initialHazmatDetails);

    if (restoredHazmatDetail) {
      handleToast(ToastType.Info, 'Previous hazmat details restored.');
    }

    hazmatPopover.open(index);
  };

  const hasHazmatValues = (detail: unknown) => {
    if (!detail || typeof detail !== 'object') return false;

    const hazmatDetail = detail as Partial<{
      unNumber: string | null;
      packingGroup: string | null;
      hazardClass: string | null;
      piece: string | null;
      contactNumber: string | null;
      contactName: string | null;
    }>;

    return [
      hazmatDetail.unNumber,
      hazmatDetail.packingGroup,
      hazmatDetail.hazardClass,
      hazmatDetail.piece,
      hazmatDetail.contactNumber,
      hazmatDetail.contactName,
    ].some((value) => Boolean(value?.trim()));
  };

  const handleHazmatPopoverClose = (index: number) => {
    const currentHazmatDetail =
      props.data().loadItems[index]?.loadItemHazmatDetail;
    if (!hasHazmatValues(currentHazmatDetail)) {
      setFields(`loadItems.${index}.hazmat`, false);
      setFields(`loadItems.${index}.loadItemHazmatDetail`, null);
    }
    hazmatPopover.close();
  };

  const getCalculationsFromLoadItems = async () => {
    if (!loadItemDimChange()) {
      setLoadItemDimChange(false);
      return;
    }

    if (props.data().isLinearFeetOverridden) {
      setFields('linearFeet', props.data().linearFeet);
      return;
    }

    props
      .data()
      .loadItems.forEach((_, idx) => setFields(`loadItems.${idx}.index`, idx));

    const loadItemCalculationInput: LoadItemCalculationInput = {
      isStackable: props.data().isStackable,
      customerId: props.data().customerId ?? null,
      loadItems: props.data().loadItems.map((item) => ({
        heightInch: item.heightInch,
        lengthInch: item.lengthInch,
        widthInch: item.widthInch,
        quantity: item.quantity,
        weight:
          item.weight == null ||
          (typeof item.weight === 'string' && item.weight === '')
            ? null
            : Number(item.weight),
        index: item.index,
        class: item.class ?? null,
      })) as LoadLineItemDimensions[],
    };

    setLoadCalculationsLoading(true);

    const response = await GetCalculationsFromLoadItems(
      loadItemCalculationInput,
    );

    const calcLookup = new Map(
      response.loadItemCalculations.map((c) => [c.index, c]),
    );

    const mergedLoadItems = props.data().loadItems.map((item) => ({
      ...item,
      ...calcLookup.get(item.index ?? 0), // cubicFeet, pcf, suggestedClass, etc.
    }));

    batch(() => {
      setFields('loadItems', mergedLoadItems); // per‑item values
      setFields('linearFeet', response.totalLinearFeet); // shipment value
      setLoadItemCalculation(response); // keep shipment totals
      setLoadItemDimChange(false);
      setLoadCalculationsLoading(false);
    });
  };

  const handleOnChangeForLinearfeet = async (index: number) => {
    if (props.data().loadItems[index]) {
      await getCalculationsFromLoadItems();
    }
  };
  const handleDeclaredValueChange = (value: number | null) => {
    if (value === null) {
      //resetting so that the store is reset and the old values dont show up
      setLtlQuoteState('falveyPricingQuote', undefined);
    }
    setFields('declaredValue', value);
  };

  onMount(() => {
    // TODO : Refactor flow for LTL
    if (props.data().loadItems.length > 0) {
      setLoadItemDimChange(true);
      void getCalculationsFromLoadItems();
    }
  });

  const calculateBreakdownSum = (
    loadItemIndex: number,
    propertyName: keyof LTLLoadItem | keyof LTLQuoteLoadItemBreakdown,
  ) => {
    const displayBreakDownArray =
      props.data().loadItems[loadItemIndex]?.quoteLoadItemBreakdown ?? [];
    if (!displayBreakDownArray.length) {
      return 0;
    }

    const allEmpty = displayBreakDownArray.every((item) => {
      if (propertyName in item) {
        const propValue = item[propertyName as keyof LTLQuoteLoadItemBreakdown];
        return (
          propValue === undefined ||
          propValue === null ||
          propValue === '' ||
          propValue === 0
        );
      }
      return true;
    });

    if (allEmpty) {
      return '';
    }

    return displayBreakDownArray.reduce((sum, item) => {
      const key = propertyName as keyof LTLQuoteLoadItemBreakdown;
      const rawValue = item[key];
      const value = Number(rawValue ?? 0);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  };

  const getHighestClassAndNmfc = (loadItemIndex: number) => {
    const displayBreakDownArray =
      props.data().loadItems[loadItemIndex]?.quoteLoadItemBreakdown ?? [];

    const result = { class: undefined, nmfc: undefined };

    if (!displayBreakDownArray.length) {
      return result;
    }

    const allClassesEmpty = displayBreakDownArray.every(
      (item) =>
        item.class == null ||
        (typeof item.class === 'string' && item.class.trim() === ''),
    );

    if (allClassesEmpty) {
      return { class: '', nmfc: '' };
    }

    const itemsWithClassData = displayBreakDownArray
      .map((item) => {
        const classValue = item.class !== undefined ? String(item.class) : null;
        if (classValue == null) return null;

        const numericValue = parseFloat(classValue.replace(/[^0-9.]/g, ''));
        return isNaN(numericValue)
          ? null
          : {
              originalValue: classValue,
              numericValue,
              nmfc: item.nmfc,
            };
      })
      .filter(Boolean);

    if (!itemsWithClassData.length) {
      return result;
    }

    const itemWithHighestClass = itemsWithClassData.reduce(
      (highest, current) => {
        if (!highest) return current;
        if (!current) return highest;

        return current.numericValue > highest.numericValue ? current : highest;
      },
      itemsWithClassData[0],
    );
    return {
      class: itemWithHighestClass?.originalValue,
      nmfc:
        itemWithHighestClass?.nmfc == null ||
        itemWithHighestClass.nmfc.trim() === ''
          ? ''
          : itemWithHighestClass.nmfc,
    };
  };
  const processBreakdownData = (index: number) => {
    const loadItem = props.data().loadItems[index];

    if (
      loadItem.quoteLoadItemBreakdown &&
      loadItem.quoteLoadItemBreakdown.length > 0
    ) {
      const { class: highestClass, nmfc: correspondingNmfc } =
        getHighestClassAndNmfc(index);

      if (highestClass !== undefined && highestClass !== loadItem.class) {
        setFields(`loadItems.${index}.class`, highestClass);
      }

      if (
        correspondingNmfc !== undefined &&
        correspondingNmfc !== loadItem.nmfc
      ) {
        setFields(`loadItems.${index}.nmfc`, correspondingNmfc);
      }

      const totalWeight = calculateBreakdownSum(index, 'weight');
      if (totalWeight !== loadItem.weight) {
        setFields(`loadItems.${index}.weight`, totalWeight);
      }

      const totalPieces = calculateBreakdownSum(index, 'pieces');
      if (totalPieces !== loadItem.pieces && totalPieces !== '') {
        setFields(`loadItems.${index}.pieces`, totalPieces);
      }
      const hasHazmat = loadItem.quoteLoadItemBreakdown.some(
        (item) => item.hazmat === true,
      );

      if (hasHazmat === false && loadItem.hazmat) {
        const otherItemsHaveHazmat = props
          .data()
          .loadItems.some((item, idx) => idx !== index && item.hazmat === true);

        if (!otherItemsHaveHazmat && props.data().shipmentAccessorials) {
          const accessorials = [...(props.data().shipmentAccessorials ?? [])];
          const hazmatIndex = accessorials.indexOf(
            'Hazmat' as unknown as ItemAccessorial,
          );
          if (hazmatIndex !== -1) {
            accessorials.splice(hazmatIndex, 1);
            setFields('shipmentAccessorials', accessorials);
          }
        }

        setFields(`loadItems.${index}.hazmat`, hasHazmat);
      }
    }
  };

  createEffect(() => {
    const loadItems = props.data().loadItems;
    if (loadItems.length > 0) {
      loadItems.forEach((item, index) => {
        if (
          item.quoteLoadItemBreakdown &&
          item.quoteLoadItemBreakdown.length > 0
        ) {
          batch(() => {
            processBreakdownData(index);
          });
        }
      });
    }
  });
  return (
    <>
      <Stack spacing={1} pt={1} position="relative">
        <Box
          class="flex flex-wrap"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="h6" sxProps={{ color: '#123B50' }}>
              Items Picking Up
            </Typography>
            <Button
              disabled={!Boolean(props.data().customerId)}
              variant="contained"
              startIcon={<PlusIcon size={20} />}
              label="Open Product Catalog"
              title="Select a specific customer to enable the product catalog"
              onClick={() => openModal(productCatalogModalId(null))}
              dataTestId={formatDataTestId('Open Product Catalog', 'button')}
            />
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              label="Declared Value"
              class="[&>input]:text-black!"
              value={String(props.data().declaredValue ?? '')}
              type="number"
              onChange={(val: string) => {
                handleDeclaredValueChange(val === '' ? null : Number(val));
              }}
              size="sm"
              fullWidth={false}
              placeholder="Add Cargo Value"
              disabled={!Boolean(props.data().customerId)}
              error={props.errors('declaredValue')}
              dataTestId="declared-value-text-input"
            />
            <Typography
              variant="h6"
              sxProps={{
                color: '#123B50',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '12px',
                maxWidth: '330px',
              }}
            >
              Please review this{' '}
              <Popover
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                classes="p-0! font-medium! text-[#026EA1]! leading-3!"
                parenDivStyle="contents"
                eleName={<>EXCLUSION LIST</>}
              >
                <ExclusionList />
              </Popover>{' '}
              to check for items not covered by insurance added to this quote.
              <span class="underline">
                ITEMS IN THIS LIST WILL NOT BE COVERED
              </span>{' '}
              in the event of a claim.
            </Typography>
          </Box>
        </Box>
        <Box
          class={`${cls.tableContainer}
          ${ConfigManager.showLayout ? 'max-w-[calc(100vw-152px)]!' : ''}
          ${loadCalculationsLoading() ? 'cursor-not-allowed' : 'cursor-pointer'}
          `}
        >
          <Table
            class={`${
              loadCalculationsLoading()
                ? 'pointer-events-none'
                : 'pointer-events-auto'
            }`}
          >
            <TableHead>
              <TableRow>
                {tableHeaders.map((header) => (
                  <TableCell
                    class="text-black font-medium border-b"
                    style={{
                      'min-width': header.minWidth,
                      'max-width': header.maxWidth,
                    }}
                  >
                    {header.title}
                    {Boolean(header.required) && (
                      <span class={cls.mandatory}> *</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <Index
                each={props
                  .data()
                  .loadItems.filter((i) => i.operationType !== 'Delete')}
              >
                {/* eslint-disable-next-line complexity */}
                {(rowData, index) => (
                  <>
                    <TableRow>
                      <TableCell style={cellStyles}>
                        <TextField
                          label=""
                          class="h-[36px]! [&>input]:text-black!"
                          value={rowData().quantity ?? undefined}
                          maxLength={2}
                          type="number"
                          onChange={(e: unknown) => {
                            if (e !== props.data().loadItems[index].quantity) {
                              setLoadItemDimChange(true);
                            }
                            setFields(
                              `loadItems.${index}.quantity`,
                              Number(e as number),
                            );
                            void handleOnChangeForLinearfeet(index);
                          }}
                          size="sm"
                          placeholder="Qty"
                          on:blur={(e: Event) => {
                            const target = e.target as HTMLInputElement;
                            if (target.value === '') {
                              setFields(`loadItems.${index}.quantity`, 0);
                            }
                          }}
                          dataTestId={`quantity-text-input-${index}`}
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <SelectInputCustom
                          class="h-[36px]!"
                          onChange={(val) => {
                            setFields(`loadItems.${index}.type`, val);
                            setFields(`loadItems.${index}.item`, val);
                          }}
                          value={
                            !!rowData().type && rowData().type !== ''
                              ? rowData().type
                              : !!rowData().item && rowData().item !== ''
                                ? rowData().item
                                : 'Pallets'
                          }
                          error={
                            props.errors(`loadItems.${index}.type`) ?? null
                          }
                          menuItems={unitTypes.map((u) => ({
                            label: u,
                            value: u,
                          }))}
                          dataTestId={formatDataTestId(
                            'LTL load item unit type',
                            'selectInput',
                          )}
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <TextField
                          label=""
                          class="h-[36px]! [&>input]:text-black!"
                          size="sm"
                          placeholder="Description"
                          value={rowData().description}
                          onChange={(e: string) =>
                            setFields(`loadItems.${index}.description`, e)
                          }
                          dataTestId={`description-text-input-${index}`}
                          endIcon={
                            (rowData().quoteLoadItemBreakdown?.length ?? 0) ===
                              0 && Boolean(props.data().customerId) ? (
                              <IconButton
                                style={{
                                  padding: '0px',
                                  height: '32px',
                                  width: '32px',
                                }}
                                on:click={(e) => {
                                  e.preventDefault();
                                  setLoadItemIndex(index);
                                  openModal(productCatalogModalId(index));
                                }}
                                size="lg"
                                aria-label="Add item from catalog"
                                dataTestId={`load-item-product-catalog-button-${index}`}
                              >
                                <PlusIcon color="grey" />
                              </IconButton>
                            ) : undefined
                          }
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <TextField
                          label=""
                          type="number"
                          placeholder="#"
                          class="h-[36px]! [&>input]:text-black!"
                          size="sm"
                          maxLength={8}
                          value={rowData().pieces ?? ''}
                          onChange={(e: unknown) =>
                            setFields(`loadItems.${index}.pieces`, e as number)
                          }
                          disabled={
                            (rowData().quoteLoadItemBreakdown?.length ?? 0) > 0
                          }
                          dataTestId={`pieces-text-input-${index}`}
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <TextField
                          label=""
                          class="h-[36px]! [&>input]:text-black!"
                          size="sm"
                          type="number"
                          maxLength={6}
                          placeholder="Weight"
                          value={rowData().weight ?? ''}
                          dataTestId={`weight-text-input-${index}`}
                          onChange={(e: unknown) => {
                            if (e !== props.data().loadItems[index].weight) {
                              setLoadItemDimChange(true);
                            }
                            setFields(`loadItems.${index}.weight`, e as number);
                            void handleOnChangeForLinearfeet(index);
                          }}
                          disabled={
                            (rowData().quoteLoadItemBreakdown?.length ?? 0) > 0
                          }
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <Stack direction="row" spacing="5px">
                          <TextField
                            label=""
                            placeholder="L"
                            maxLength={3}
                            type="number"
                            class="h-[36px]! [&>input]:text-black!"
                            size="sm"
                            value={rowData().lengthInch ?? ''}
                            onChange={(e: unknown) => {
                              if (
                                e !== props.data().loadItems[index].lengthInch
                              ) {
                                setLoadItemDimChange(true);
                              }
                              setFields(
                                `loadItems.${index}.lengthInch`,
                                e as number,
                              );
                              void handleOnChangeForLinearfeet(index);
                            }}
                            dataTestId={`length-text-input-${index}`}
                          />
                          <TextField
                            label=""
                            placeholder="W"
                            maxLength={3}
                            type="number"
                            class="h-[36px]! [&>input]:text-black!"
                            size="sm"
                            value={rowData().widthInch ?? ''}
                            onChange={(e: unknown) => {
                              if (
                                e !== props.data().loadItems[index].widthInch
                              ) {
                                setLoadItemDimChange(true);
                              }
                              setFields(
                                `loadItems.${index}.widthInch`,
                                e as number,
                              );
                              void handleOnChangeForLinearfeet(index);
                            }}
                            dataTestId={`width-text-input-${index}`}
                          />
                          <TextField
                            label=""
                            placeholder="H"
                            type="number"
                            maxLength={3}
                            class="h-[36px]! [&>input]:text-black!"
                            size="sm"
                            value={rowData().heightInch ?? ''}
                            onChange={(e: unknown) => {
                              if (
                                e !== props.data().loadItems[index].heightInch
                              ) {
                                setLoadItemDimChange(true);
                              }
                              setFields(
                                `loadItems.${index}.heightInch`,
                                e as number,
                              );
                              void handleOnChangeForLinearfeet(index);
                            }}
                            dataTestId={`height-text-input-${index}`}
                          />
                        </Stack>
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <TextField
                          label=""
                          maxLength={10}
                          class="h-[36px]! [&>input]:text-black!"
                          size="sm"
                          placeholder="NMFC"
                          value={rowData().nmfc as string}
                          onChange={(e: string) =>
                            setFields(`loadItems.${index}.nmfc`, e)
                          }
                          dataTestId="dataTestId"
                          disabled={
                            (rowData().quoteLoadItemBreakdown?.length ?? 0) > 0
                          }
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <SelectInputCustom
                          style={{ width: '100%' }}
                          class="h-[36px]!"
                          label=""
                          value={rowData().class?.toString() ?? ''}
                          onChange={(val) => {
                            setFields(`loadItems.${index}.class`, val);
                          }}
                          menuItems={freightClasses.map((fc) => ({
                            label: fc,
                            value: fc,
                          }))}
                          dataTestId={formatDataTestId(
                            'LTL load item freight class',
                            'selectInput',
                          )}
                          disabled={
                            (rowData().quoteLoadItemBreakdown?.length ?? 0) > 0
                          }
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <div
                          class={cls.hazmatPopOverContainer}
                          data-hazmat-row-index={index}
                        >
                          <Checkbox
                            size="xs"
                            color="info"
                            label=""
                            style={LTlFormStyles.hazmatCheckBox}
                            checked={Boolean(rowData().hazmat)}
                            onChange={() => handleHazmatChange(rowData, index)}
                            disabled={
                              (rowData().quoteLoadItemBreakdown?.length ?? 0) >
                              0
                            }
                            dataTestId={`hazmat-checkbox-input-${index}`}
                          />
                          <Popover
                            forceClosePopover={!rowData().hazmat}
                            triggerClose={hazmatPopover.triggerClose(index)}
                            onClick={hazmatPopover.onTriggerClick(index)}
                            preventFormSubmit
                            eleName={
                              <PencilIcon
                                color={rowData().hazmat ? '#026EA1' : 'gray'}
                                dataTestId={`hazmat-edit-icon-${index}`}
                              />
                            }
                            classes={cls.hazmatPopOver}
                            sxProps={LTlFormStyles.hazmatPopOver}
                            anchorOrigin={{
                              vertical: 'top',
                              horizontal: 'left',
                            }}
                            transformOrigin={{
                              vertical: 'bottom',
                              horizontal: 'left',
                            }}
                            onOutsideClose={() =>
                              handleHazmatPopoverClose(index)
                            }
                          >
                            <HazardousMaterial
                              fromSidebar={true}
                              loadItemHazmatDetail={
                                props.data().loadItems[index]
                                  ?.loadItemHazmatDetail ?? initialHazmatDetails
                              }
                              onSave={(hazmatDetail) => {
                                setCachedHazmatDetails((prev) => ({
                                  ...prev,
                                  [index]: hazmatDetail,
                                }));

                                setFields(
                                  `loadItems.${index}.loadItemHazmatDetail`,
                                  hazmatDetail,
                                );
                                setFields(`loadItems.${index}.hazmat`, true);
                                hazmatPopover.close();
                              }}
                              onClose={() => handleHazmatPopoverClose(index)}
                            />
                          </Popover>
                        </div>
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <TextField
                          label="PCF"
                          size="md"
                          class={summaryCalculationFieldClasses}
                          value={
                            loadCalculationsLoading() // <— global flag you already have
                              ? '…' // or <SmallSpinner />
                              : (rowData().pcf ?? '')
                          }
                          dataTestId={`pcf-text-input-${index}`}
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <TextField
                          label="Cubic Feet"
                          size="md"
                          class={summaryCalculationFieldClasses}
                          value={
                            loadCalculationsLoading()
                              ? '…'
                              : (rowData().cubicFeet ?? '')
                          }
                          dataTestId={`cubic-feet-text-input-${index}`}
                        />
                      </TableCell>
                      <TableCell style={cellStyles}>
                        <div class="flex">
                          <Show when={props.mixedCommoditiesSettingEnabled()}>
                            <IconButton
                              on:click={(e) => {
                                e.preventDefault();
                                const quoteLoadItemBreakdown = [
                                  ...(props.data().loadItems[index]
                                    .quoteLoadItemBreakdown ?? []),
                                  DEFAULT_LOAD_ITEM_BREAKDOWN,
                                ];
                                setFields(
                                  `loadItems.${index}.quoteLoadItemBreakdown`,
                                  quoteLoadItemBreakdown,
                                );
                              }}
                              dataTestId={`add-load-item-breakdown-button-${index}`}
                            >
                              <SubdirectoryArrowRightIcon class="text-[#026EA1]" />
                            </IconButton>
                          </Show>
                          <IconButton
                            on:click={(e) => {
                              e.preventDefault();
                              remapHazmatCacheAfterDelete(index);
                              const filteredLoadItems = props
                                .data()
                                .loadItems.filter((item, i) => i !== index);
                              setFields('loadItems', filteredLoadItems);
                              if (filteredLoadItems.length === 0) {
                                setFields('linearFeet', '');
                                resetLoadItemCalculation();
                              }
                              if (filteredLoadItems.length !== 0) {
                                setLoadItemDimChange(true);
                                void getCalculationsFromLoadItems();
                              }
                            }}
                            dataTestId={`delete-load-item-button-${index}`}
                          >
                            <DeleteIcon class={cls.deleteIcon} />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                    <QuoteLoadItemBreakdown
                      rowData={rowData}
                      loadItemIndex={index}
                      data={props.data}
                      setFields={setFields}
                      errors={(field: string) => Boolean(props.errors(field))}
                      handleOnChangeForLinearfeet={handleOnChangeForLinearfeet}
                      getCalculationsFromLoadItems={
                        getCalculationsFromLoadItems
                      }
                      setLoadItemDimChange={setLoadItemDimChange}
                      customerId={props.data().customerId}
                    />
                    <SuggestLineItemClass
                      data={props.data}
                      index={index}
                      setFields={setFields}
                      suggestedClassSettingEnabled={
                        props.suggestedClassSettingEnabled
                      }
                    />
                    <TableRowErrors
                      columnsLength={tableHeaders.length}
                      tableErrors={createErrors(index)}
                      tableRowClasses="bg-white!"
                    />
                    <Show when={!isPublicApiWarningsEnabled()}>
                      <Show
                        when={singleWeightTooMuch(
                          props.data().loadItems[index],
                        )}
                      >
                        <TableRowErrors
                          type="warning"
                          columnsLength={11}
                          tableErrors={[
                            `The single item weight entered is over ${LTLWeightLimit.Single} lbs, possibly exceeding weight limitations. Please reach out to LTL@armstrongtransport.com for assistance with pricing.`,
                          ]}
                          tableRowClasses="bg-white! border-none!"
                        />
                      </Show>
                      <Show
                        when={
                          (props.data().loadItems[index]?.lengthInch ?? 0) >
                            95 ||
                          (props.data().loadItems[index]?.widthInch ?? 0) > 95
                        }
                      >
                        <TableRowErrors
                          type="warning"
                          columnsLength={11}
                          tableRowClasses="bg-white! border-none!"
                          tableErrors={[
                            'The length or width entered qualifies as an overlength item. These charges will be included in the quote.',
                          ]}
                        />
                      </Show>
                    </Show>
                  </>
                )}
              </Index>
            </TableBody>
            <TableFoot>
              <TableRow style={LTlFormStyles.tableFooterRow}>
                <TableCell style={{ border: '0' }}>
                  <IconButton
                    size="sm"
                    on:click={(e) => {
                      e.preventDefault();
                      const loadItems = [
                        ...props.data().loadItems,
                        DEFAULT_LOAD_ITEM,
                      ];
                      setFields('loadItems', loadItems);
                    }}
                    dataTestId="add-load-item-button"
                  >
                    <PlusCircleOutlineIcon class="text-[#026EA1]!" />
                  </IconButton>
                </TableCell>
                <TableCell colSpan={3} style={{ border: '0' }}></TableCell>
                <TableCell colSpan={3} style={{ border: '0' }}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    gap={2}
                  >
                    <Box flex={1}>
                      <TextField
                        label="Total Linear Feet"
                        fullWidth
                        class={`h-[36px]! ${
                          props.data().isLinearFeetOverridden
                            ? '[&>input]:text-black!'
                            : '[&>input]:text-gray-500! border-transparent! hover:border-transparent! focus-within:outline-none! focus-within:border-transparent!'
                        }`}
                        disabled={!Boolean(props.data().isLinearFeetOverridden)}
                        forceLabelShrink
                        value={props.data().linearFeet}
                        onChange={(e: string) => setFields('linearFeet', e)}
                        size="sm"
                        style={{
                          width: '140px',
                          color: '#000000',
                          'font-weight': '400',
                        }}
                        dataTestId="total-linear-feet-text-input"
                      />
                    </Box>
                    <Box flex={1} display="flex" gap={0.2} alignItems="center">
                      <Checkbox
                        size="xs"
                        color="info"
                        label="Override"
                        checked={props.data().isLinearFeetOverridden}
                        onChange={async (e) => {
                          setFields('isLinearFeetOverridden', e);
                          setLoadItemDimChange(true);
                          await getCalculationsFromLoadItems();
                          if (!e) {
                            setFields('linearFeet', props.data().linearFeet);
                          }
                        }}
                        dataTestId={formatDataTestId(
                          'Override',
                          'checkboxInput',
                        )}
                      />
                      <Tooltip
                        title="The Total Linear Feet is a vital element of the LTL Quote Tool, ensuring accurate estimations for efficient freight management. Users can manually adjust this calculated value by enabling the 'Override' checkbox."
                        dataTestId="total-linear-feet-override-tooltip"
                      >
                        <InformationIcon class="text-[#026EA1]! " />
                      </Tooltip>
                    </Box>
                    <Box flex={1}>
                      <Checkbox
                        size="xs"
                        color="info"
                        label="Stackable"
                        checked={props.data().isStackable}
                        onChange={(e) => {
                          setFields('isStackable', e);
                          setLoadItemDimChange(true);
                          void getCalculationsFromLoadItems();
                        }}
                        dataTestId={formatDataTestId(
                          'Stackable',
                          'checkboxInput',
                        )}
                      />
                    </Box>
                  </Box>
                </TableCell>
                <TableCell colSpan={2} style={LTlFormStyles.totalWeightCell}>
                  <TextField
                    label="Total Shipment Weight"
                    value={getTotalWeight(props.data().loadItems)}
                    size="md"
                    class={`${summaryCalculationFieldClasses} ${
                      getTotalWeight(props.data().loadItems) >
                      LTLWeightLimit.Total
                        ? 'bg-red-100!'
                        : ''
                    }`}
                    dataTestId="total-shipment-weight-text-input"
                  />
                </TableCell>
                <TableCell colSpan={1} style={LTlFormStyles.shipmentTotalCell}>
                  <TextField
                    label="Shipment PCF"
                    value={
                      loadCalculationsLoading()
                        ? '—'
                        : loadItemCalculationState.items.totalPcf
                    }
                    size="md"
                    class={summaryCalculationFieldClasses}
                    dataTestId="shipment-pcf-text-input"
                  />
                </TableCell>
                <TableCell colSpan={1} style={LTlFormStyles.shipmentTotalCell}>
                  <TextField
                    label="Total Cubic Feet"
                    value={
                      loadCalculationsLoading()
                        ? '—'
                        : loadItemCalculationState.items.totalCubicFeet
                    }
                    size="md"
                    class={summaryCalculationFieldClasses}
                    dataTestId="total-cubic-feet-text-input"
                  />
                </TableCell>
                <TableCell style={{ border: '0' }}></TableCell>
              </TableRow>
              <Show
                when={isPublicApiWarningsEnabled()}
                fallback={
                  <>
                    <Show
                      when={
                        loadItemCalculationState.items.totalAdjustedPcf < 6 &&
                        loadItemCalculationState.items.totalAdjustedCubicFeet >=
                          750
                      }
                    >
                      <TableRowErrors
                        type="warning"
                        columnsLength={11}
                        tableErrors={[
                          'The calculated shipment cube may exceed 750 cubic feet and the shipment density may be less than 6 PCF. This may result in additional charges. Please review the carriers rules tariff and/or secure a volume quote for this shipment.',
                        ]}
                        tableRowClasses="bg-white!"
                      />
                    </Show>
                    <Show when={Number(props.data().linearFeet) > 12}>
                      <TableRowErrors
                        type="warning"
                        columnsLength={11}
                        tableErrors={[
                          // eslint-disable-next-line prettier/prettier
                          'The shipment exceeds 12 linear feet. This may result in additional charges. Please review the carrier\'s rules tariff and/or secure a volume quote for this shipment.',
                        ]}
                        tableRowClasses="bg-white!"
                      />
                    </Show>
                  </>
                }
              >
                <TableRowErrors
                  type="tableError"
                  columnsLength={11}
                  tableErrors={calculationMessages('error')}
                  tableRowClasses="bg-white!"
                />
                <TableRowErrors
                  type="warning"
                  columnsLength={11}
                  tableErrors={calculationMessages('warning')}
                  tableRowClasses="bg-white!"
                />
                <TableRowErrors
                  type="info"
                  columnsLength={11}
                  tableErrors={calculationMessages('info')}
                  tableRowClasses="bg-white!"
                />
              </Show>
            </TableFoot>
          </Table>
        </Box>
      </Stack>
      <BasicModal
        id={productCatalogModalId()}
        footer={false}
        width="1300px"
        maxWidth="95vw"
        title=" Select LTL Product Catalog"
        onClose={() => {
          closeModal(productCatalogModalId());
          setLoadItemIndex(null);
        }}
      >
        <Show when={isModalOpen(productCatalogModalId())}>
          <LTLProductCatalog
            customerId={props.data().customerId!}
            goBackButtonText="< Back to LTL Quoting"
            goBack={() => closeModal(productCatalogModalId())}
            onRowClick={(rowData: LTLCatalogs) => {
              loadItemIndex() !== null
                ? addListItemFromLTLCatalog(rowData, loadItemIndex()!)
                : addListItemFromLTLCatalog(rowData);
              setLoadItemIndex(null);
            }}
            closeModal={closeModal}
            openModal={openModal}
          />
        </Show>
      </BasicModal>
    </>
  );
};
